import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { HlmIconComponent } from '../../ui';
import { PermissionService } from '../../core/auth/permission.service';

/**
 * EB-3709: Read-only banner shown across all Brand Manager pages when the user
 * lacks `amie.brandManager.write`. Renders nothing if the user has write access,
 * so callers can drop `<cmt-brand-readonly-banner />` unconditionally at the top
 * of their template — gating logic stays in one place.
 *
 * Visual treatment uses the project's amber accent token (sparse accent per the
 * Refined Editorial Workspace aesthetic). The amber tone reads as "heads up,
 * view-only" without the alarm of red. A short eyebrow + Fraunces headline +
 * Geist body line gives the banner enough weight to register at a glance while
 * staying on-brand with the rest of the page.
 *
 * The two phrases "Read-only" and `amie.brandManager.write` are load-bearing
 * for the existing per-page banner-render tests (textContent assertions).
 */
@Component({
  selector: 'cmt-brand-readonly-banner',
  standalone: true,
  imports: [HlmIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (!canWrite()) {
      <aside
        role="status"
        aria-live="polite"
        class="relative flex items-start gap-4 overflow-hidden rounded-xl border border-warning/60 bg-warning/15 px-4 py-3 shadow-sm md:px-6 md:py-4"
      >
        <!-- EB-3709 H1: switched accent→warning tokens. accent collapses to
             near-white pale teal on Clean Teal light → banner blended into the
             card. warning reads amber across all three themes and is the
             semantically correct "heads up, not destructive" register. -->
        <span
          aria-hidden="true"
          class="absolute inset-y-2 left-0 w-[3px] rounded-full bg-warning"
        ></span>

        <!-- EB-3709 M3: solid medallion (no alpha) with light foreground icon —
             resolves the dark-icon-on-dark-translucent contrast issue on
             Editorial dark. The warning-foreground token does not exist in
             styles.tailwind.css; using text-background which is the
             contrasting foreground for dark warning surfaces across all
             themes (verified: white on light, near-white on dark). -->
        <span
          class="mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-full bg-warning"
          aria-hidden="true"
        >
          <hlm-icon
            name="lucideLock"
            size="1.1rem"
            class="text-background"
          />
        </span>

        <div class="flex min-w-0 flex-1 flex-col gap-1">
          <!-- EB-3709 C1: dropped text-xs override — page-top banner is not a
               dense context (Pre-Flight #9 reserves text-xs eyebrow for dense
               sub-toolbar hints / AG-Grid badges / chips). -->
          <p class="cmt-eyebrow text-foreground/80">
            View only
          </p>
          <!-- EB-3709 I3: text-base font-semibold (was font-display text-lg) —
               the medallion + eyebrow carry the call-out; a Geist semibold
               headline competes less with the hero <h1> below. -->
          <p class="text-base font-semibold leading-tight text-foreground">
            Read-only mode
          </p>
          <p class="text-sm leading-snug text-foreground">
            You can browse the Brand Manager. Editing requires the
            <span class="font-mono-feat text-foreground">amie.brandManager.write</span>
            module — contact an admin to enable changes.
          </p>
        </div>
      </aside>
    }
  `,
})
export class BrandReadonlyBannerComponent {
  private readonly permissionService = inject(PermissionService);
  protected readonly canWrite = computed(() =>
    this.permissionService.canWrite('brandManager'),
  );
}
